import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ApolloError } from '@apollo/client';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import Helmet from '../../../components/Helmet';
import CircularProgress from '../../../components/_loading/CircularProgress';
import Context from '../../../context';
import { useCompanyQuery } from '../../../../graphql/query/company/useCompanyQuery';
import NotFound from '../../../components/_error/NotFound';
import BusinessLayout from '../../../layout/Business';
import { useUpdateCompanyMarketCoverageMutation } from '../../../../graphql/mutation/company/updateCompanyMarketCoverageMutation';
import useForm from '../../../components/_hook/useForm';
import useNotification from '../../../components/_hook/useNotification';
import useLanguage from '../../../components/_hook/useLanguage';
import StickyFormFooter from '../../../components/_form/StickyFormFooter';
import { IValues } from '../../../components/_select/_module/Select';
import SelectAfrica from './_module/SelectAfrica';
import SelectAsia from './_module/SelectAsia';
import SelectEurope from './_module/SelectEurope';
import SelectOceania from './_module/SelectOceania';
import SelectAmerica from './_module/SelectAmerica';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
  contentPaper: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
    width: '100%',
  },
  title: {
    margin: theme.spacing(1),
  },
  footerPaper: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    width: '100%',
    position: 'sticky',
    bottom: 0,
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
}));

function BusinessMarketCoverage() {
  interface IFormValue {
    exportAreaAfrica: string[];
    exportAreaAmerica: string[];
    exportAreaAsia: string[];
    exportAreaEurope: string[];
    exportAreaOceania: string[];
    importAreaAfrica: string[];
    importAreaAmerica: string[];
    importAreaAsia: string[];
    importAreaEurope: string[];
    importAreaOceania: string[];
    serviceAreaAfrica: string[];
    serviceAreaAmerica: string[];
    serviceAreaAsia: string[];
    serviceAreaEurope: string[];
    serviceAreaOceania: string[];
  }

  const classes = useStyles();
  const { notification } = useNotification();
  const { t } = useTranslation();
  const { getText } = useLanguage();
  const { company } = useContext(Context);
  const companyFragment = `
    _id
    name {
        la
        text
    }
    infos {
      trade_capability {
        export_area {
          africa 
          america
          asia
          europe 
          oceania
        }
        import_area {
          africa 
          america
          asia
          europe 
          oceania
        }
        service_area {
          africa 
          america
          asia
          europe 
          oceania
        }
      }
    }
  `;
  const { data, loading } = useCompanyQuery(companyFragment, {
    variables: {
      filter: {
        _id: company._id as string,
      },
      limit: 1,
    },
  });

  function onCompleted() {
    notification.success(t('done'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateCompanyMarketCoverageMutation,
    { loading: isUpdatingCompanyMarketCoverage },
  ] = useUpdateCompanyMarketCoverageMutation(
    `updatedCompany {
				${companyFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (data) {
      await updateCompanyMarketCoverageMutation({
        variables: {
          _id: data.companies.items[0]._id,
          exportAreaAfrica: tempValues.exportAreaAfrica,
          exportAreaAmerica: tempValues.exportAreaAmerica,
          exportAreaAsia: tempValues.exportAreaAsia,
          exportAreaEurope: tempValues.exportAreaEurope,
          exportAreaOceania: tempValues.exportAreaOceania,
          importAreaAfrica: tempValues.importAreaAfrica,
          importAreaAmerica: tempValues.importAreaAmerica,
          importAreaAsia: tempValues.importAreaAsia,
          importAreaEurope: tempValues.importAreaEurope,
          importAreaOceania: tempValues.importAreaOceania,
          serviceAreaAfrica: tempValues.serviceAreaAfrica,
          serviceAreaAmerica: tempValues.serviceAreaAmerica,
          serviceAreaAsia: tempValues.serviceAreaAsia,
          serviceAreaEurope: tempValues.serviceAreaEurope,
          serviceAreaOceania: tempValues.serviceAreaOceania,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      exportAreaAfrica: [],
      exportAreaAmerica: [],
      exportAreaAsia: [],
      exportAreaEurope: [],
      exportAreaOceania: [],
      importAreaAfrica: [],
      importAreaAmerica: [],
      importAreaAsia: [],
      importAreaEurope: [],
      importAreaOceania: [],
      serviceAreaAfrica: [],
      serviceAreaAmerica: [],
      serviceAreaAsia: [],
      serviceAreaEurope: [],
      serviceAreaOceania: [],
    },
    onSubmit: onSubmitForm,
  });

  useEffect(() => {
    if (!loading && data && data.companies.items.length) {
      const tempCompany = data.companies.items[0];
      const companyInfos = tempCompany.infos || {};
      const companyInfoTradeCapability = companyInfos.trade_capability || {};
      const companyInfoTradeCapabilityExportArea =
        companyInfoTradeCapability.export_area || {};
      const companyInfoTradeCapabilityImportArea =
        companyInfoTradeCapability.import_area || {};
      const companyInfoTradeCapabilityServiceArea =
        companyInfoTradeCapability.service_area || {};

      setValues({
        exportAreaAfrica: companyInfoTradeCapabilityExportArea.africa,
        exportAreaAmerica: companyInfoTradeCapabilityExportArea.america,
        exportAreaAsia: companyInfoTradeCapabilityExportArea.asia,
        exportAreaEurope: companyInfoTradeCapabilityExportArea.europe,
        exportAreaOceania: companyInfoTradeCapabilityExportArea.oceania,
        importAreaAfrica: companyInfoTradeCapabilityImportArea.africa,
        importAreaAmerica: companyInfoTradeCapabilityImportArea.america,
        importAreaAsia: companyInfoTradeCapabilityImportArea.asia,
        importAreaEurope: companyInfoTradeCapabilityImportArea.europe,
        importAreaOceania: companyInfoTradeCapabilityImportArea.oceania,
        serviceAreaAfrica: companyInfoTradeCapabilityServiceArea.africa,
        serviceAreaAmerica: companyInfoTradeCapabilityServiceArea.america,
        serviceAreaAsia: companyInfoTradeCapabilityServiceArea.asia,
        serviceAreaEurope: companyInfoTradeCapabilityServiceArea.europe,
        serviceAreaOceania: companyInfoTradeCapabilityServiceArea.oceania,
      });
    }
  }, [loading]);

  const queryCompany = data ? data.companies.items[0] : null;

  if (loading) return <CircularProgress center color="primary" />;
  if (!queryCompany) return <NotFound />;

  return (
    <Grid container>
      <Helmet title={`${t('market_coverage')} | ${t('business')}`} />
      <Grid container item className={classes.title}>
        <Typography variant="h6">
          {t('update')} {getText(company.name)} {t('market_coverage')}
        </Typography>
      </Grid>
      <Paper square className={classes.contentPaper}>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={3}>
            <Grid container item xs={12} spacing={2}>
              <Grid container item xs={12} spacing={1}>
                <Grid container item xs={12} alignItems="center">
                  <ImportExportIcon />
                  &nbsp;
                  <Typography variant="h6">{t('export_zone')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <SelectAfrica<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.exportAreaAfrica}
                  onChange={(options) =>
                    setValue(
                      'exportAreaAfrica',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.exportAreaAfrica)}
                  helperText={errors.exportAreaAfrica}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectAmerica<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.exportAreaAmerica}
                  onChange={(options) =>
                    setValue(
                      'exportAreaAmerica',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.exportAreaAmerica)}
                  helperText={errors.exportAreaAmerica}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectAsia<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.exportAreaAsia}
                  onChange={(options) =>
                    setValue(
                      'exportAreaAsia',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.exportAreaAsia)}
                  helperText={errors.exportAreaAsia}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectEurope<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.exportAreaEurope}
                  onChange={(options) =>
                    setValue(
                      'exportAreaEurope',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.exportAreaEurope)}
                  helperText={errors.exportAreaEurope}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectOceania<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.exportAreaOceania}
                  onChange={(options) =>
                    setValue(
                      'exportAreaOceania',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.exportAreaOceania)}
                  helperText={errors.exportAreaOceania}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper square className={classes.contentPaper}>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={3}>
            <Grid container item xs={12} spacing={2}>
              <Grid container item xs={12} spacing={1}>
                <Grid container item xs={12} alignItems="center">
                  <ImportExportIcon />
                  &nbsp;
                  <Typography variant="h6">{t('import_zone')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <SelectAfrica<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.importAreaAfrica}
                  onChange={(options) =>
                    setValue(
                      'importAreaAfrica',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.importAreaAfrica)}
                  helperText={errors.importAreaAfrica}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectAmerica<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.importAreaAmerica}
                  onChange={(options) =>
                    setValue(
                      'importAreaAmerica',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.importAreaAmerica)}
                  helperText={errors.importAreaAmerica}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectAsia<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.importAreaAsia}
                  onChange={(options) =>
                    setValue(
                      'importAreaAsia',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.importAreaAsia)}
                  helperText={errors.importAreaAsia}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectEurope<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.importAreaEurope}
                  onChange={(options) =>
                    setValue(
                      'importAreaEurope',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.importAreaEurope)}
                  helperText={errors.importAreaEurope}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectOceania<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.importAreaOceania}
                  onChange={(options) =>
                    setValue(
                      'importAreaOceania',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.importAreaOceania)}
                  helperText={errors.importAreaOceania}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper square className={classes.contentPaper}>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={3}>
            <Grid container item xs={12} spacing={2}>
              <Grid container item xs={12} spacing={1}>
                <Grid container item xs={12} alignItems="center">
                  <LocalMallIcon />
                  &nbsp;
                  <Typography variant="h6">{t('service_zone')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <SelectAfrica<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.serviceAreaAfrica}
                  onChange={(options) =>
                    setValue(
                      'serviceAreaAfrica',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.serviceAreaAfrica)}
                  helperText={errors.serviceAreaAfrica}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectAmerica<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.serviceAreaAmerica}
                  onChange={(options) =>
                    setValue(
                      'serviceAreaAmerica',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.serviceAreaAmerica)}
                  helperText={errors.serviceAreaAmerica}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectAsia<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.serviceAreaAsia}
                  onChange={(options) =>
                    setValue(
                      'serviceAreaAsia',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.serviceAreaAsia)}
                  helperText={errors.serviceAreaAsia}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectEurope<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.serviceAreaEurope}
                  onChange={(options) =>
                    setValue(
                      'serviceAreaEurope',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.serviceAreaEurope)}
                  helperText={errors.serviceAreaEurope}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectOceania<IValues>
                  isClearable
                  isMulti
                  fullWidth
                  value={values.serviceAreaOceania}
                  onChange={(options) =>
                    setValue(
                      'serviceAreaOceania',
                      options.map((option) => option.value)
                    )
                  }
                  error={Boolean(errors.serviceAreaOceania)}
                  helperText={errors.serviceAreaOceania}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper square className={classes.footerPaper}>
        <Grid container spacing={2}>
          <StickyFormFooter
            onSubmit={handleSubmit}
            isSubmitting={isUpdatingCompanyMarketCoverage}
          />
        </Grid>
      </Paper>
    </Grid>
  );
}

export default () => <BusinessLayout main={<BusinessMarketCoverage />} />;
